// Copyright (c) StrangeBee 2023

import React, { ReactNode, createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Provider, StatusOutput } from 'thehive-sdk';
import client, { queryClient } from '@Client';
import { ICONS } from '@Constants/iconsApp';
import { useLocalMessage } from '@Helpers/locale';
import { GlobalStreamSrv, GlobalStreamLongPollingSrv } from '@Services/StreamSrv';
import { Modal } from 'antd';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { useQuery } from 'react-query';
import Countdown from 'antd/lib/statistic/Countdown';
import { useRouteMatch } from 'react-router-dom';
import RouteBuilder from '@Services/RouteBuilder';

export const AutoLogoutContext = createContext({
    refetch: async () => [] as Provider[] as any,
    clearInactivityTimer: () => {}, // eslint-disable-line
    restartInactivityTimer: () => {}, // eslint-disable-line
});

type AutoLogoutProviderProps = {
    children: ReactNode;
};

type AuthProvider = {
    timeout: string;
    warning: string;
    inactivity: {
        warning: string;
        timeout: string;
    };
};

type modaleProps = {
    display: boolean;
    message: ReactNode | null;
    title: string | null;
    cta: string | null;
};

export const AutoLogoutProvider = ({ children }: AutoLogoutProviderProps) => {
    const [inactivityTimer, setInactivityTimer] = useState<number>(0);
    const [modal, setModal] = useState<modaleProps>({ display: false, message: null, title: null, cta: null });
    const [sessionConf, setSessionConf] = useState<AuthProvider | undefined>();
    const i = useLocalMessage();
    const elapsedInactivityMinutes = useRef<number>(0); // time spent in timer
    // Init Broadcast Channel to avoid logging out with activity on another navigator tab
    const channelRef = useRef<BroadcastChannel | null>(null);

    const match = useRouteMatch(RouteBuilder.compute('dashboards.details'));

    const query = useQuery<StatusOutput, string>(
        ['get-session-timers'],
        () => client.StatusController.getSessionConfig('private'),
        {
            enabled: true,
            onSuccess: (result) => {
                setSessionConf({
                    warning: result.$headers['x-th-session-warning'],
                    timeout: result.$headers['x-th-session-timeout'],
                    inactivity: {
                        warning: result.$headers['x-th-inactivity-warning'],
                        timeout: result.$headers['x-th-inactivity-timeout'],
                    },
                });
            },
        },
    );

    // start parsing headers props
    const timeoutInactivityInMinutes = useMemo(
        () => Number(sessionConf?.inactivity?.timeout) / 60,
        [sessionConf?.inactivity?.timeout],
    );
    const warningInactivityInMinutes = useMemo(
        () => Number(sessionConf?.inactivity?.warning) / 60,
        [sessionConf?.inactivity?.warning],
    );
    const warningSessionInMinutes = useMemo(() => Number(sessionConf?.warning) / 60 || 5, [sessionConf?.warning]);
    // end parsing headers props

    const handleLogout = useCallback(() => {
        client.AuthController.logout().then(() => {
            queryClient.refetchQueries('currentUser');
            GlobalStreamSrv.cancel();
            GlobalStreamLongPollingSrv.cancelFeed();
        });
        clearTimers();
    }, []);

    const clearTimers = () => {
        const lastTimerId = window.setTimeout(() => {
            for (let i = lastTimerId; i >= 0; i--) {
                window.clearInterval(i);
            }
        }, 0);
    };

    const clearInactivityTimer = () => {
        window.clearInterval(inactivityTimer);
        window.removeEventListener('mousemove', handleUserActivity);
        window.removeEventListener('keydown', handleUserActivity);
    };

    const restartInactivityTimer = () => {
        query.refetch();
        resetTimeout();
        window.addEventListener('mousemove', handleUserActivity);
        window.addEventListener('keydown', handleUserActivity);
    };

    const resetTimeout = useCallback(() => {
        elapsedInactivityMinutes.current = 0; // Reset time spent in timer
    }, []);

    const handleInactivityModal = useCallback(() => {
        const newInactivityTimer = window.setInterval(() => {
            elapsedInactivityMinutes.current++;
            if (elapsedInactivityMinutes.current === timeoutInactivityInMinutes - warningInactivityInMinutes) {
                setModal({
                    display: true,
                    message: (
                        <Countdown
                            value={
                                Date.now() + (timeoutInactivityInMinutes - elapsedInactivityMinutes.current) * 60 * 1000
                            }
                            valueRender={(value) => (
                                <span>
                                    {i('configPlateform.authentication.inactivity.message')}
                                    {value}
                                    {i('configPlateform.authentication.inactivity.messageEnd')}
                                </span>
                            )}
                            className="AutoLogoutModal__Countdown"
                            onFinish={() => handleLogout()}
                        />
                    ),
                    title: i('configPlateform.authentication.inactivity.detected'),
                    cta: i('configPlateform.authentication.inactivity.cta'),
                });
            }
        }, 60 * 1000);

        setInactivityTimer(newInactivityTimer);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timeoutInactivityInMinutes, warningInactivityInMinutes]);

    const handleUserActivity = useCallback(() => {
        if (channelRef.current) {
            channelRef.current.postMessage({ activity: true });
        }
        resetTimeout();
    }, [channelRef, resetTimeout]);

    // handle session expiration
    useEffect(() => {
        if (sessionConf) {
            // handle landing on Dashboard page directly => don't trigger inactivity timer
            // when leaving Dashboard page, it triggers restartInactivityTimer()
            if (!match) {
                handleInactivityModal();
            }
            // handle GMT format and date parsing
            const expSessionDate = new Date(sessionConf?.timeout);
            const warningTime = new Date( // setMinutes return a timestamp, need to reparse it throught Date constructor
                // can't use expSessionDate because it will change the original value, need to recalculate it
                new Date(sessionConf?.timeout).setMinutes(expSessionDate.getMinutes() - warningSessionInMinutes),
            );

            const delay = expSessionDate.getTime() - warningTime.getTime();

            window.setInterval(() => {
                const now = new Date();
                // display modal
                if (now.getHours() === warningTime.getHours() && now.getMinutes() === warningTime.getMinutes()) {
                    setModal({
                        display: true,
                        message: (
                            <Countdown
                                value={Date.now() + delay}
                                valueRender={(value) => (
                                    <span>
                                        {value}
                                        {i('configPlateform.authentication.session.message')}
                                    </span>
                                )}
                                className="AutoLogoutModal__Countdown"
                            />
                        ),
                        title: i('configPlateform.authentication.session.detected'),
                        cta: i('app.general.ok'),
                    });
                }
                if (now >= expSessionDate) {
                    handleLogout();
                }
            }, 60 * 1000);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionConf]);

    // remove trackers when modal is displayed
    useEffect(() => {
        if (modal.display) {
            window.removeEventListener('mousemove', handleUserActivity);
            window.removeEventListener('keydown', handleUserActivity);
        } else {
            window.addEventListener('mousemove', handleUserActivity);
            window.addEventListener('keydown', handleUserActivity);
        }
    }, [modal.display, handleUserActivity]);

    // start & remove user's activity tracker & inactivity timer
    useEffect(() => {
        query.refetch();
        // user activities listeners

        return () => {
            window.removeEventListener('mousemove', handleUserActivity);
            window.removeEventListener('keydown', handleUserActivity);
            // clear setInterval to avoid memory leak
            clearTimers();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // handle multi-tab
    useEffect(() => {
        channelRef.current = new BroadcastChannel('inactivityChannel');
        // Listen Broadcast Channel messages
        channelRef.current.onmessage = (event) => {
            if (event.data.activity) {
                // user is active in another tab, reset timeout in all concerned tabs and remove modal
                resetTimeout();
            }
        };

        // clean up
        return () => {
            if (channelRef.current) {
                channelRef.current.close();
                channelRef.current = null;
            }
        };
    }, [channelRef, resetTimeout]);

    return (
        <AutoLogoutContext.Provider
            value={{
                refetch: query.refetch,
                restartInactivityTimer,
                clearInactivityTimer,
            }}
        >
            <Modal
                title={modal.title}
                visible={modal.display}
                closeIcon={<ICONS.CloseIcon color="grey" />}
                closable
                className="AutoLogoutModal"
                footer={
                    <THButton onClick={() => setModal({ display: false, message: null, title: null, cta: null })}>
                        {modal.cta}
                    </THButton>
                }
            >
                {modal.message}
            </Modal>
            {children}
        </AutoLogoutContext.Provider>
    );
};
